import api from './api';

export interface HealthStatus {
  status: 'ok' | 'degraded' | 'error';
  timestamp: number;
  uptime: number;
  service: string;
}

export interface DetailedHealthStatus extends HealthStatus {
  version?: string;
  environment?: string;
  checks?: {
    database?: {
      status: string;
      latency: number | null;
      connection: string;
    };
    memory?: {
      usage: any;
      percentage: number;
    };
    cpu?: {
      loadAvg: number[];
    };
  };
}

class HealthService {
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private listeners: ((isHealthy: boolean) => void)[] = [];
  private isHealthy: boolean = true;
  private lastHealthCheck: number = 0;

  // Health Check starten
  startHealthCheck(intervalMs: number = 10000) {
    // Initial Check
    this.checkHealth();

    // Periodic Check
    this.healthCheckInterval = setInterval(() => {
      this.checkHealth();
    }, intervalMs);

    console.log(`Health Check gestartet - Intervall: ${intervalMs}ms`);
  }

  // Health Check stoppen
  stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
      console.log('Health Check gestoppt');
    }
  }

  // Listener für Health Status Änderungen
  onHealthChange(callback: (isHealthy: boolean) => void) {
    this.listeners.push(callback);
    
    // Aktuellen Status sofort zurückgeben
    callback(this.isHealthy);
    
    // Cleanup Function zurückgeben
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Aktueller Health Status
  getCurrentHealthStatus(): boolean {
    return this.isHealthy;
  }

  // Letzter Health Check Zeitstempel
  getLastHealthCheck(): number {
    return this.lastHealthCheck;
  }

  // Basic Health Check
  // 2026-09-22: erst nach ZWEI Fehlschlägen in Folge offline melden – ein einzelner
  // langsamer Health-Call (z. B. Backend direkt nach Neustart, viele parallele Requests)
  // darf nicht sofort das Wartungs-Overlay auslösen
  static readonly OFFLINE_AFTER_FAILURES = 2;
  static readonly HEALTH_TIMEOUT_MS = 8000;
  private consecutiveFailures = 0;

  private async checkHealth(): Promise<void> {
    try {
      const response = await api.get('/api/health', {
        timeout: HealthService.HEALTH_TIMEOUT_MS
      });

      const wasHealthy = this.isHealthy;
      this.consecutiveFailures = 0;
      this.isHealthy = response.status === 200 && response.data?.status === 'ok';
      this.lastHealthCheck = Date.now();

      // Nur Listener benachrichtigen wenn sich Status geändert hat
      if (wasHealthy !== this.isHealthy) {
        console.log(`Backend Health Status geändert: ${this.isHealthy ? 'HEALTHY' : 'UNHEALTHY'}`);
        this.notifyListeners();
      }

    } catch (error) {
      const wasHealthy = this.isHealthy;
      this.consecutiveFailures += 1;
      this.lastHealthCheck = Date.now();
      if (this.consecutiveFailures < HealthService.OFFLINE_AFTER_FAILURES) {
        console.warn(`Backend Health Check fehlgeschlagen (${this.consecutiveFailures}/${HealthService.OFFLINE_AFTER_FAILURES}), warte auf nächste Prüfung`);
        return;
      }
      this.isHealthy = false;

      if (wasHealthy !== this.isHealthy) {
        console.warn('Backend Health Check fehlgeschlagen:', error);
        this.notifyListeners();
      }
    }
  }

  /** Nur für Tests: eine Prüfung ausführen */
  async runCheckOnce(): Promise<void> {
    await this.checkHealth();
  }

  // Detailed Health Check (on-demand)
  async getDetailedHealth(): Promise<DetailedHealthStatus | null> {
    try {
      const response = await api.get('/api/health/detailed', {
        timeout: 10000 // 10 Sekunden Timeout für detailed check
      });

      if (response.status === 200) {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error('Detailed Health Check fehlgeschlagen:', error);
      return null;
    }
  }

  // Alle Listener benachrichtigen
  private notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.isHealthy);
      } catch (error) {
        console.error('Health Check Listener Error:', error);
      }
    });
  }

  // Health Check mit Retry
  async checkHealthWithRetry(maxRetries: number = 3): Promise<boolean> {
    let attempts = 0;
    
    while (attempts < maxRetries) {
      try {
        await this.checkHealth();
        if (this.isHealthy) {
          return true;
        }
      } catch (error) {
        console.warn(`Health Check Attempt ${attempts + 1} failed:`, error);
      }
      
      attempts++;
      
      // Kurz warten zwischen Versuchen (exponential backoff)
      if (attempts < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempts) * 1000));
      }
    }
    
    return false;
  }

  // Graceful Shutdown
  cleanup() {
    this.stopHealthCheck();
    this.listeners = [];
  }
}

export default new HealthService();